import { useState, useEffect, useRef } from "react";
import { Menu, X, ChevronDown } from "lucide-react";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Globe } from "lucide-react";

// ==================== TYPES ====================
interface NavItem {
  name: string;
  href?: string;
  type: "link" | "dropdown";
  items?: { name: string; href: string }[];
}

// ==================== DATA ====================
const NAV_ITEMS: NavItem[] = [
  {
    name: "services",
    type: "dropdown",
    items: [
      { name: "automation_control", href: "/automation-detail" },
      { name: "research_development", href: "/research-detail" },
      { name: "innovation_agriculture", href: "/quality-detail" },
      { name: "climate_fertigation", href: "/fertigation-systems" },
    ],
  },
  { name: "projects", href: "/projects", type: "link" },
  { name: "portfolio", href: "/portfolio", type: "link" },
  { name: "blog", href: "/blog", type: "link" },
];

const DARK_NAV_ROUTES = ["/contact", "/blog"];

// ==================== MAIN COMPONENT ====================
export default function NewNav() {
  // State
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
  const [isVisible, setIsVisible] = useState(true);
  const [lastScrollY, setLastScrollY] = useState(0);
  const [isScrolled, setIsScrolled] = useState(false);

  // Refs & Location
  const navRef = useRef<HTMLDivElement>(null);
  const dropdownTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const location = useLocation();
  const forceDarkNav = DARK_NAV_ROUTES.includes(location.pathname);
  const { t, i18n } = useTranslation();

  const changeLanguage = (lang: string) => {
    i18n.changeLanguage(lang);
    localStorage.setItem("i18nextLng", lang);
  };

  useEffect(() => {
    const savedLang = localStorage.getItem("i18nextLng") || "en";
    i18n.changeLanguage(savedLang);
  }, [i18n]);

  // ==================== SCROLL HANDLER ====================
  useEffect(() => {
    let scrollTimeout: ReturnType<typeof setTimeout>;

    const handleScroll = () => {
      if (openDropdown) return;

      const currentScrollY = window.scrollY;

      // Show/Hide navbar
      if (currentScrollY > lastScrollY && currentScrollY > 100) {
        setIsVisible(false);
      } else if (currentScrollY < lastScrollY) {
        setIsVisible(true);
      }

      // Background effect
      if (!forceDarkNav) {
        setIsScrolled(currentScrollY > 50);
      }

      clearTimeout(scrollTimeout);
      scrollTimeout = setTimeout(() => setIsVisible(true), 300);
      setLastScrollY(currentScrollY);
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", handleScroll);
      clearTimeout(scrollTimeout);
    };
  }, [lastScrollY, openDropdown, forceDarkNav]);

  // ==================== PAGE CHANGE HANDLER ====================
  useEffect(() => {
    setIsScrolled(forceDarkNav);
    setLastScrollY(0);
    window.scrollTo(0, 0);
  }, [location.pathname, forceDarkNav]);

  // ==================== CLICK OUTSIDE HANDLER ====================
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (navRef.current && !navRef.current.contains(event.target as Node)) {
        setOpenDropdown(null);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // ==================== FUNCTIONS ====================
  const toggleDropdown = (name: string) => {
    setIsVisible(true);
    setOpenDropdown((prev) => (prev === name ? null : name));
  };

  const closeMenu = () => {
    setIsMenuOpen(false);
    setOpenDropdown(null);
  };

  const handleDropdownEnter = (name: string) => {
    if (dropdownTimerRef.current) {
      clearTimeout(dropdownTimerRef.current);
      dropdownTimerRef.current = null;
    }
    setOpenDropdown(name);
  };

  const handleDropdownLeave = () => {
    dropdownTimerRef.current = setTimeout(() => {
      setOpenDropdown(null);
    }, 150);
  };

  // ==================== RENDER ====================
  return (
    <>
      {/* NAVBAR */}
      <nav
        ref={navRef}
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${isVisible ? "translate-y-0" : "-translate-y-full"
          } ${isScrolled ? "bg-[#002f37] backdrop-blur-md shadow-lg" : "bg-transparent"}`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            {/* Logo */}
            <Link to="/" className="flex items-center" aria-label="Homepage">
              <img
                src="/Logo.webp"
                alt="Saleem Enterprises logo"
                loading="lazy"
                width="200"
                height="50"
                className="h-12 w-auto"
              />
            </Link>

            {/* Desktop Nav */}
            <div className="hidden lg:flex items-center gap-2">
              {NAV_ITEMS.map((item) =>
                item.type === "dropdown" ? (
                  <div
                    key={item.name}
                    className="relative"
                    onMouseEnter={() => handleDropdownEnter(item.name)}
                    onMouseLeave={handleDropdownLeave}
                  >
                    <button
                      onClick={() => toggleDropdown(item.name)}
                      className="px-4 py-2 flex items-center gap-1 text-white hover:text-[#7EDE56] transition"
                    >
                      {t(item.name)}
                      <ChevronDown
                        className={`w-4 h-4 transition-transform ${openDropdown === item.name ? "rotate-180" : ""
                          }`}
                      />
                    </button>

                    {openDropdown === item.name && (
                      <div
                        className="absolute top-full left-0 pt-2"
                        onMouseEnter={() => handleDropdownEnter(item.name)}
                        onMouseLeave={handleDropdownLeave}
                      >
                        <div className="w-64 bg-white rounded-lg shadow-xl overflow-hidden">
                          {item.items?.map((sub, i) => (
                            <Link
                              key={i}
                              to={sub.href}
                              onClick={() => setOpenDropdown(null)}
                              className="block px-4 py-3 text-sm text-gray-800 hover:bg-gray-100 transition-colors"
                            >
                              {t(sub.name)}
                            </Link>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <Link
                    key={item.name}
                    to={item.href!}
                    className="px-4 py-2 text-white hover:text-[#7EDE56] transition"
                  >
                    {t(item.name)}
                  </Link>
                )
              )}
            </div>

            {/* Desktop Buttons */}
            <div className="hidden lg:flex items-center gap-3">

              <div className="relative">
                <button
                  onClick={() => toggleDropdown("lang")}
                  className="flex items-center gap-2 px-3 py-2 rounded-lg bg-gray-800 text-white hover:bg-gray-700 transition"
                >
                  <Globe className="w-4 h-4" />

                  {/* ✅ Active Language Short Code */}
                  <span className="text-sm font-medium uppercase">
                    {i18n.resolvedLanguage || "EN"}
                  </span>
                </button>

                {openDropdown === "lang" && (
                  <div className="absolute right-0 mt-2 w-36 bg-white rounded-lg shadow-lg overflow-hidden">
                    <button
                      onClick={() => changeLanguage("en")}
                      className={`block w-full text-left px-4 py-2 text-sm hover:bg-gray-100 ${i18n.language === "en" ? "bg-gray-200 font-semibold" : ""
                        }`}
                    >
                      English
                    </button>

                    <button
                      onClick={() => changeLanguage("ur")}
                      className={`block w-full text-left px-4 py-2 text-sm hover:bg-gray-100 ${i18n.language === "ur" ? "bg-gray-200 font-semibold" : ""
                        }`}
                    >
                      اردو
                    </button>

                    <button
                      onClick={() => changeLanguage("tr")}
                      className={`block w-full text-left px-4 py-2 text-sm hover:bg-gray-100 ${i18n.language === "tr" ? "bg-gray-200 font-semibold" : ""
                        }`}
                    >
                      Türkçe
                    </button>
                  </div>
                )}
              </div>
              <Link
                to="/contact"
                className="px-6 py-2.5 bg-[#7EDE56] text-[#002F3F] font-medium rounded-lg hover:bg-[#6bc945] transition"
              >
                {t("getInTouch")}
              </Link>
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsMenuOpen(true)}
              className="lg:hidden text-white bg-white/10 p-2.5 rounded-lg"
              aria-label="Open navigation menu"
            >
              <Menu className="w-6 h-6" />
            </button>
          </div>
        </div>
      </nav>

      {/* MOBILE OVERLAY */}
      <div
        onClick={closeMenu}
        className={`fixed inset-0 bg-black/80 z-40 lg:hidden transition-opacity ${isMenuOpen ? "opacity-100 visible" : "opacity-0 invisible"
          }`}
      />

      {/* MOBILE MENU */}
      <div
        className={`fixed top-0 right-0 h-full w-80 bg-white z-50 lg:hidden overflow-y-auto transition-transform duration-300 ${isMenuOpen ? "translate-x-0" : "translate-x-full"
          }`}
      >
        <div className="p-6">
          <button
            onClick={closeMenu}
            className="absolute top-6 right-6 z-10"
            aria-label="Close navigation menu"
          >
            <X className="w-6 h-6" />
          </button>

          <div className="mt-12 space-y-1">
            {NAV_ITEMS.map((item) =>
              item.type === "dropdown" ? (
                <div key={item.name} className="border-b border-gray-200">
                  <button
                    onClick={() => toggleDropdown(item.name)}
                    className="w-full flex justify-between items-center py-3 font-medium text-gray-900"
                  >
                    {t(item.name)}  {/* ✅ was: {item.name} */}
                    <ChevronDown
                      className={`w-5 h-5 transition-transform duration-200 ${openDropdown === item.name ? "rotate-180" : ""
                        }`}
                    />
                  </button>

                  <div
                    className={`overflow-hidden transition-all duration-300 ${openDropdown === item.name
                      ? "max-h-96 opacity-100"
                      : "max-h-0 opacity-0"
                      }`}
                  >
                    <div className="pl-4 pb-3 space-y-1">
                      {item.items?.map((sub, i) => (
                        <Link
                          key={i}
                          to={sub.href}
                          onClick={closeMenu}
                          className="block py-2 text-sm text-gray-600 hover:text-[#7EDE56] transition-colors"
                        >
                          {t(sub.name)}  {/* ✅ was: {sub.name} */}
                        </Link>
                      ))}
                    </div>
                  </div>
                </div>
              ) : (
                <Link
                  key={item.name}
                  to={item.href!}
                  onClick={closeMenu}
                  className="block py-3 font-medium text-gray-900 border-b border-gray-200"
                >
                  {t(item.name)}  {/* ✅ was: {item.name} */}
                </Link>
              )
            )}
          </div>

          <div className="mt-8 space-y-3">
            <select
              value={i18n.language}
              onChange={(e) => changeLanguage(e.target.value)}
              className="w-full py-2.5 border border-gray-300 rounded-lg"
              aria-label="Language selector mobile"
            >
              <option value="en">{t("en")}</option>
              <option value="ur">{t("ur")}</option>
              <option value="tr">{t("tr")}</option>
            </select>
            <Link
              to="/contact"
              onClick={closeMenu}
              className="block w-full py-2.5 bg-[#7EDE56] text-black text-center font-medium rounded-lg hover:bg-[#6bc945] transition"
            >
              {t("getInTouch")}
            </Link>
          </div>
        </div>
      </div>
    </>
  );
}